// Clean electric guitar for the guitar style (docs/AUDIO_DESIGN.md §12): a steel string on a neck pickup through a warm
// amp, a stereo chorus and a short spring. Every pluck is a Karplus–Strong string rendered in JS into an AudioBuffer
// (cached per page; no AudioWorklet, and a DelayNode loop can't go under 128 samples), then played by one
// AudioBufferSource + one gain per note through a shared bus. The lead shares the plucks and the bus (slides and
// hammer-ons bend the ringing buffer's playbackRate, like a finger moving on the same string).
import { rng, clamp } from '../../engine/core.js';
import { mtof, hold } from '../dsp.js';
import { gain, filter, osc, panner, persistent } from '../instruments.js';

/* ---------------- plucks: a steel string read by a neck pickup, rendered at half the context rate ---------------- */
// y[n] = e[n] for the first lap (n < L), then T(D(H(y[n − L])))   e = the pluck, H = loss + damping one-pole matched to
// the T60 at f0 and at 2.5 kHz (so the upper harmonics die first), D = stiffness allpass, T = tuning allpass. The
// pickup sits a quarter of the string from the bridge: it hears y[n] − .8·y[n − P/4] (a gentle comb: harmonics 4, 8, …
// dip by 14 dB, the warm neck tone) and its coil gives the string's velocity (the first difference). Half rate: the bus
// low-pass sits at 2.4–3.8 kHz, nothing above 11 kHz is lost; ≈ 0.4 ms and 150–270 KB per pluck, ≤ ~45 plucks a page.
const CACHE = new Map();                              // `${rate}:${midi}` → AudioBuffer, built once, lazily
export const PLUCK_STATS = { built: 0, ms: 0, maxMs: 0 };
const FB = 2500, PICKUP = 0.25, COMB = 0.8;
//   pick position β (from the bridge) · flesh LP of the excitation (Hz) · noise share (the string scrape) · attack ramp (s)
//   · T60 at 2.5 kHz (s). Notes under E3 are the thumb's (round), the rest fingertips (steel is brighter than nylon).
const ROLES = {
  thumb: { beta: 0.2, lp: 1400, noise: 0.1, atk: 0.004, tb: 0.5 },
  finger: { beta: 0.14, lp: 2800, noise: 0.16, atk: 0.0018, tb: 0.8 },
};
export const roleOf = midi => (midi < 52 ? 'thumb' : 'finger');
const TWO_PI = Math.PI * 2;
const apDelay = (c, w) => 1 - 2 * Math.atan2(c * Math.sin(w), 1 + c * Math.cos(w)) / w;   // 1st-order allpass phase delay

function render(rate, midi) {
  const R = ROLES[roleOf(midi)], f0 = mtof(midi), P = rate / f0, w0 = TWO_PI * f0 / rate, wb = TWO_PI * FB / rate;
  // steel sustains: T60 at f0 = 6.4·(110/f)^.42 s (E2 7.2 s · A3 4.8 s · E4 4 s · E5 3 s); the buffer is half of it, 1.6–2.8 s
  const T0 = clamp(6.4 * Math.pow(110 / f0, 0.42), 2.2, 8), N = Math.round(clamp(0.5 * T0, 1.6, 2.8) * rate);
  // loss + damping H(z) = g(1 − a)/(1 − a z⁻¹): |H| = 10^(−3/(f0·T60)) at f0 and at 2.5 kHz (bisection on a)
  const A0 = Math.pow(10, -3 / (f0 * T0)), want = Math.min(1, Math.pow(10, -3 / (f0 * R.tb)) / A0);
  const mag = (a, w) => (1 - a) / Math.sqrt(1 - 2 * a * Math.cos(w) + a * a);
  let lo = 0, hi = 0.97;
  for (let k = 0; k < 40; k++) { const m = (lo + hi) / 2; if (mag(m, wb) / mag(m, w0) > want) lo = m; else hi = m; }
  const a = (lo + hi) / 2, b0 = (A0 / mag(a, w0)) * (1 - a);
  // stiffness (wound strings more) and tuning: the loop's phase delay at f0 must be P
  const cd = midi < 50 ? -0.15 : -0.08, rest = P - Math.atan2(a * Math.sin(w0), 1 - a * Math.cos(w0)) / w0 - apDelay(cd, w0);
  const L = Math.max(2, Math.floor(rest - 0.5)), d = rest - L;
  let cl = -0.9, ch = 0.9;
  for (let k = 0; k < 40; k++) { const m = (cl + ch) / 2; if (apDelay(m, w0) > d) cl = m; else ch = m; }
  const C = (cl + ch) / 2;
  // excitation = the first lap: a triangle pluck peaking at β + noise combed at β, rounded by the flesh (a periodic one-pole
  // LP run twice, 12 dB/oct), mean removed; the loop filters are primed with it so lap 2 follows lap 1 without a step
  const pk = Math.max(1, Math.round(R.beta * L)), e = new Float64Array(L), nz = new Float64Array(L);
  const r = rng((midi * 104729 + 31) >>> 0), al = 1 - Math.exp(-TWO_PI * R.lp / rate);
  for (let i = 0; i < L; i++) nz[i] = r() * 2 - 1;
  let ns = 0, mean = 0;
  const nc = Float64Array.from(nz, (v, i) => v - nz[(i - pk + L) % L]);
  for (let i = 0; i < L; i++) ns += nc[i] * nc[i];
  const nk = R.noise * 0.3 / (Math.sqrt(ns / L) || 1);
  for (let i = 0; i < L; i++) e[i] = (1 - R.noise) * (i <= pk ? i / pk : (L - i) / (L - pk)) + nk * nc[i];
  let z = 0, z2 = 0;
  for (let lap = 0; lap < 2; lap++) for (let i = 0; i < L; i++) { z += al * (e[i] - z); z2 += al * (z - z2); if (lap) e[i] = z2; }
  for (let i = 0; i < L; i++) mean += e[i] / L;
  for (let i = 0; i < L; i++) e[i] -= mean;
  let h = 0, dx = 0, dy = 0, tx = 0, ty = 0;
  const lapf = x => {
    h = b0 * x + a * h;
    const dv = cd * h + dx - cd * dy; dx = h; dy = dv;
    const tv = C * dv + tx - C * ty; tx = dv; ty = tv;
    return tv;
  };
  for (let i = 0; i < L; i++) lapf(e[i]);
  const y = new Float32Array(N);
  for (let n = 0; n < N; n++) y[n] = n < L ? e[n] : lapf(y[n - L]);
  // pickup comb → velocity (1st difference) → DC blocker (~20 Hz) → attack ramp (thumb 4 ms, finger 1.8 ms) → 0.3 s end
  // fade → RMS of 0–0.3 s = 0.2
  const D = Math.max(1, Math.round(PICKUP * P)), hp = 1 - TWO_PI * 20 / rate, na = Math.max(2, Math.round(R.atk * rate));
  const nf = Math.round(0.3 * rate), n3 = Math.min(N, Math.round(0.3 * rate)), out = new Float32Array(N);
  // before the release the string holds its plucked shape (the first lap, periodic), so the comb and the difference
  // start from rest instead of a step
  let pl = y[0] - COMB * y[(L - D % L) % L], px = 0, py = 0, ss = 0;
  for (let n = 0; n < N; n++) {
    const pu = y[n] - COMB * (n >= D ? y[n - D] : y[(n - D + L * 4) % L]), s = pu - pl; pl = pu;
    py = s - px + hp * py; px = s;
    let v = py;
    if (n < na) v *= 0.5 - 0.5 * Math.cos(Math.PI * n / na);
    if (n >= N - nf) v *= 0.5 + 0.5 * Math.cos(Math.PI * (n - N + nf) / nf);
    out[n] = v;
    if (n < n3) ss += v * v;
  }
  const k = 0.2 / (Math.sqrt(ss / n3) || 1);
  for (let n = 0; n < N; n++) out[n] *= k;
  return out;
}

/** The cached pluck of one note, rendered on first use. */
export function pluck(ac, midi) {
  const rate = ac.sampleRate >= 44100 ? Math.round(ac.sampleRate / 2) : ac.sampleRate, key = `${rate}:${midi}`;
  let b = CACHE.get(key);
  if (!b) {
    const t = performance.now(), y = render(rate, midi);
    b = ac.createBuffer(1, y.length, rate);
    b.getChannelData(0).set(y);
    CACHE.set(key, b);
    const ms = performance.now() - t;
    PLUCK_STATS.built++; PLUCK_STATS.ms += ms; PLUCK_STATS.maxMs = Math.max(PLUCK_STATS.maxMs, ms);
  }
  return b;
}

/* ---------------- the instrument ---------------- */
// Level knobs: the guitar against the EP it replaces (post-amp), the thumb against the fingers, the lead against the chords.
export const GTR = { level: 2.65, thumb: 1, send: 0.2, lead: 1.5 };
const PANS = [-0.17, -0.03, 0.13];                    // bass strings · middle · top strings
const CURVES = new Map();
/** amp warmth: y = (tanh(k(x + .1)) − tanh(.1k))/k (unity slope at 0, even + odd harmonics as it leans in), 2049 points */
function ampCurve(k) {
  const key = k.toFixed(2);
  if (!CURVES.has(key)) {
    const n = 2049, c = new Float32Array(n), off = Math.tanh(k * 0.1);
    for (let i = 0; i < n; i++) c[i] = (Math.tanh(k * ((i / (n - 1)) * 2 - 1 + 0.1)) - off) / k;
    CURVES.set(key, c);
  }
  return CURVES.get(key);
}
const delay = (ac, t, max = 0.1) => { const d = ac.createDelay(max); d.delayTime.value = t; return d; };

/**
 * createElectric(ac, out, send, p, opts) → { chord, note, warm, stats, dispose, fxIn }   (the style's `ep`)
 * p: { lp (tone), drive (1.4–2.2), chorus: { rate, cents }, spring (wet), pan }. opts: { registry, tag, cap, r, t0, log, stepDur }.
 * Bus: 3 panners → amp WaveShaper (drive k, 2x oversampled, none in lite) → HP 60 Hz → tone LP p.lp (Q 1.1: the pickup's
 * resonance) → fxIn → chorus (dry .78 + delays 7.5 / 11.5 ms swept in antiphase by a sine at p.chorus.rate, ±cents, wet .42
 * each panned ∓.6) → level → out, reverb .2, spring (HP 380 → 33 ms loop, fb .38, allpasses 1.9k / 3.1k, LP 2.8k, wet p.spring).
 * A string plucked again damps its last note (τ 12 ms); the engine's note lengths lift the fingers at every chord change.
 */
export function createElectric(ac, out, send, p, { registry, tag, cap = 16, r = Math.random, t0 = ac.currentTime, log = null, stepDur = 0.2 }) {
  const nodes = [], N = x => (nodes.push(x), x);
  const pans = PANS.map(x => N(panner(ac, clamp(x + (p.pan || 0), -1, 1)))), busIn = N(gain(ac, 1)), amp = N(ac.createWaveShaper());
  amp.curve = ampCurve(p.drive || 1.8); amp.oversample = cap < 16 ? 'none' : '2x';
  const hp = N(filter(ac, 'highpass', 60, 0.6)), tone = N(filter(ac, 'lowpass', p.lp, 1.1)), fxIn = N(gain(ac, 1));
  for (const q of pans) q.connect(busIn);
  busIn.connect(amp); amp.connect(hp); hp.connect(tone); tone.connect(fxIn);
  // chorus: ±c ¢ at rate f is a sine sweep of c / (1731·2π·f) s
  const rate = p.chorus.rate, dev = p.chorus.cents / (1731 * TWO_PI * rate);
  const dry = N(gain(ac, 0.78)), dL = N(delay(ac, 0.0075)), dR = N(delay(ac, 0.0115)), wL = N(gain(ac, 0.42)), wR = N(gain(ac, 0.42));
  const pL = N(panner(ac, -0.6)), pR = N(panner(ac, 0.6)), lfo = N(osc(ac, 'sine', rate)), lfoL = N(gain(ac, dev)), lfoR = N(gain(ac, -dev));
  const outG = N(gain(ac, GTR.level)), sendG = N(gain(ac, GTR.send));
  fxIn.connect(dry); dry.connect(outG);
  fxIn.connect(dL); dL.connect(wL); wL.connect(pL); pL.connect(outG);
  fxIn.connect(dR); dR.connect(wR); wR.connect(pR); pR.connect(outG);
  lfo.connect(lfoL); lfoL.connect(dL.delayTime); lfo.connect(lfoR); lfoR.connect(dR.delayTime);
  outG.connect(out); outG.connect(sendG); sendG.connect(send);
  // spring: a short dispersive loop (the drip), dark and quiet
  const sHP = N(filter(ac, 'highpass', 380, 0.7)), sD = N(delay(ac, 0.033)), ap1 = N(filter(ac, 'allpass', 1900, 0.6)), ap2 = N(filter(ac, 'allpass', 3100, 0.6));
  const sLP = N(filter(ac, 'lowpass', 2800, 0.6)), sFb = N(gain(ac, 0.38)), sWet = N(gain(ac, p.spring ?? 0.14));
  outG.connect(sHP); sHP.connect(sD); sD.connect(ap1); ap1.connect(ap2); ap2.connect(sLP); sLP.connect(sFb); sFb.connect(sD); sLP.connect(sWet); sWet.connect(out);
  const bus = persistent(registry, tag, [lfo], nodes, t0);
  let voices = [], maxActive = 0, steals = 0, notes = 0, pending = [];

  const damp = (x, t, tau = 0.012) => { registry.stop(x.v, t, tau); x.end = t; if (x.rec) x.rec[1] = Math.max(x.rec[0], Math.min(x.rec[1], t)); };
  /** one pluck. muted: a dead note (the fretting hand's "chk"), gone in ~40 ms */
  function note(t, midi, vel, dur, relTau = 0.08, muted = false) {
    const buf = pluck(ac, midi);
    voices = voices.filter(x => x.end > t);
    for (const x of voices) if (x.midi === midi) damp(x, t);
    voices = voices.filter(x => x.end > t);
    if (voices.length >= cap) { damp(voices.shift(), t, 0.01); steals++; }       // steal the oldest (10 ms)
    const pr = Math.pow(2, (r() * 4 - 2) / 1200);                                 // ±2 ¢ per pluck
    const src = ac.createBufferSource(), g = gain(ac, 0);
    src.buffer = buf; src.playbackRate.value = pr;
    src.connect(g); g.connect(pans[midi < 52 ? 0 : midi < 64 ? 1 : 2]);
    // 0.6·v^1.3 into the amp (the attacks of a strum peak ≈ .5–.8 and the curve takes 2–5 dB off them, the ringing stays
    // clean); release: lifting the finger, τ ≥ 60 ms
    const A = 0.6 * Math.pow(vel, 1.3) * (midi < 52 ? GTR.thumb : 1) * (muted ? 1.4 : 1);
    const off = muted ? t + 0.012 : t + Math.max(0.05, dur), tau = muted ? 0.016 : Math.max(0.06, relTau);
    g.gain.setValueAtTime(A, t);
    g.gain.setTargetAtTime(0, off, tau);
    const bufEnd = t + buf.duration / pr, end = Math.min(bufEnd, off + tau * 7);
    src.start(t); src.stop(end);
    const v = registry.add({ src: [src], all: [src, g], start: t, end, tag, out: g });
    const x = { v, midi, end: Math.min(bufEnd, off + tau * 4), rec: null };
    if (log) { x.rec = [t, x.end]; log.push(x.rec); }
    voices.push(x);
    notes++;
    maxActive = Math.max(maxActive, voices.length);
  }
  /** held chords (intro, break, outro: rel ≥ .1): a slow roll up every half bar, the thumb + a soft top pinch in between;
   *  the final chord (rel ≥ .5) is one slower roll left to ring */
  function held(t, midis, vel, dur, relTau) {
    const half = 8 * stepDur, final = relTau >= 0.5, n = final ? 1 : Math.max(1, Math.min(4, Math.floor((dur + 0.05) / half)));
    const gap = final ? 0.12 : clamp(0.5 * stepDur, 0.05, 0.1);
    for (let j = 0; j < n; j++) {
      const tj = t + j * half, left = dur - j * half;
      if (j % 2 === 0) midis.forEach((m, k) => {
        const dt = k * gap * (1 + (r() - 0.5) * 0.25);
        note(tj + dt, m, vel * (j ? 0.85 : 1) * (0.9 + 0.04 * k), left - dt, relTau);
      });
      else {
        note(tj, midis[0], vel * 0.8, left, relTau);
        midis.slice(-2).forEach((m, k) => note(tj + 0.012 + 0.007 * k, m, vel * 0.66, left - 0.012, relTau));
      }
    }
  }
  return {
    note, busIn, fxIn,
    /** notes in the order given: bottom-up is a down-strum (spread `strum`, the top +6 %), top-down an up-strum (60 % of the
     *  spread, lighter); one or two notes are a finger or a pinch; a short ghost (vel < .28, ≤ 1.3 steps) is a muted strum */
    chord(t, midis, vel, dur, strum = 0.02, relTau = 0.08) {
      const n = midis.length;
      if (relTau >= 0.095 && n >= 3 && dur >= 7.5 * stepDur) held(t, midis, vel, dur, relTau);
      else if (vel < 0.28 && dur <= 1.3 * stepDur) midis.forEach((m, k) => note(t + k * 0.006, m, vel, 0, relTau, true));
      else if (n <= 2) midis.forEach((m, k) => note(t + k * 0.008, m, vel * (k ? 0.92 : 1), dur - k * 0.008, relTau));
      else {
        const up = midis[0] > midis[n - 1], spread = up ? strum * 0.6 : strum;
        midis.forEach((m, k) => {
          const dt = (k * spread / (n - 1)) * (1 + (r() - 0.5) * 0.3), acc = up ? (k ? 0.8 : 0.9) : k === n - 1 ? 1.06 : 0.94;
          note(t + dt, m, Math.min(1, vel * acc * (1 + (r() * 2 - 1) * 0.04)), dur - dt, relTau);
        });
      }
      if (pending.length) pluck(ac, pending.shift());
    },
    /** render these notes' plucks: within `ms` of JS now, the rest one per chord call */
    warm(midis, ms = 3) {
      const t = performance.now();
      for (const m of new Set(midis)) { if (performance.now() - t < ms) pluck(ac, m); else pending.push(m); }
    },
    stats: () => ({ maxActive, steals, notes, active: voices.length }),
    dispose(t) { bus.dispose(t); },
  };
}

/* ---------------- lead: single notes on the same strings, with slides, hammer-ons and a finger vibrato ---------------- */
/**
 * createElectricLead(ac, gtr, send, lead, opts) → { note, dispose }. lead: { timbre ('neck' | 'bright'), pan }.
 * Plucks → gain → presence peak 2.2 kHz (+3.5 dB bright, 0 neck; Q .8) → pan → the guitar's own amp, tone, chorus and
 * spring (busIn); dotted-8th echo (fb .25, LP 2.2k, wet .28) into its chorus (fxIn); reverb .3. A legato note (the
 * engine's glide) within 2 semitones is a hammer-on / pull-off (the ringing buffer's playbackRate steps, ×1.3 / ×1.1
 * re-excite, p .8), 3–5 semitones a slide (τ 30 ms, p .6); otherwise a new pluck that damps a last note within a 4th
 * (the same string, τ 25 ms). The first note of a phrase slides up a tone into
 * place (p .3). Notes over 0.45 s get a vibrato: 5.2 Hz ±11 ¢ from 0.22 s. Up to 3 voices.
 */
export function createElectricLead(ac, gtr, send, lead, { registry, tag, stepDur, r = Math.random, t0 = ac.currentTime }) {
  const nodes = [], N = x => (nodes.push(x), x);
  const inG = N(gain(ac, 1)), pres = N(filter(ac, 'peaking', 2200, 0.8, lead.timbre === 'bright' ? 3.5 : 0));
  const pan = N(panner(ac, lead.pan || 0)), sendG = N(gain(ac, 0.3));
  const dly = N(delay(ac, Math.min(1.9, 3 * stepDur), 2)), dLP = N(filter(ac, 'lowpass', 2200, 0.5)), fb = N(gain(ac, 0.25)), wet = N(gain(ac, 0.28));
  inG.connect(pres); pres.connect(pan); pan.connect(gtr.busIn); pan.connect(sendG); sendG.connect(send);
  pan.connect(dly); dly.connect(dLP); dLP.connect(fb); fb.connect(dly); dLP.connect(wet); wet.connect(gtr.fxIn);
  const bus = registry.add({ src: [], all: nodes, start: t0, end: Infinity, tag: `${tag}:p` });
  let voices = [], last = null;
  const vibrato = len => {
    const n = Math.max(8, Math.round(len * 5.2 * 16)), c = new Float32Array(n);
    for (let i = 0; i < n; i++) { const s = (i / (n - 1)) * len; c[i] = 11 * Math.sin(TWO_PI * 5.2 * s) * Math.min(1, s / 0.25, (len - s) / 0.1); }
    return c;
  };
  return {
    note(t, midi, vel, dur, glide = false) {
      dur = Math.max(0.06, dur);
      voices = voices.filter(x => x.end > t);
      const iv = last ? midi - last.now : 99, ringing = last && last.end > t + 0.02, a = Math.abs(iv);
      if (glide && ringing && a && a <= 5 && r() < (a <= 2 ? 0.8 : 0.6)) {
        const x = last, ratio = x.pr * Math.pow(2, (midi - x.midi) / 12), prm = x.src.playbackRate, g = x.g.gain;
        hold(prm, t); hold(g, t);
        if (a <= 2) { prm.setValueAtTime(ratio, t); g.setTargetAtTime(x.A * (iv > 0 ? 1.3 : 1.1), t, 0.004); }
        else prm.setTargetAtTime(ratio, t, 0.03);
        const off = t + dur, end = off + 0.09 * 7;
        g.setTargetAtTime(0, off, 0.09);
        try { x.src.stop(end); } catch { /* ended */ }
        x.v.end = Math.max(x.v.end, end); x.end = off + 0.09 * 4; x.now = midi;
        return;
      }
      if (ringing && a <= 4) { registry.stop(last.v, t, 0.025); last.end = t; }
      voices = voices.filter(x => x.end > t);
      if (voices.length >= 3) { const o = voices.shift(); registry.stop(o.v, t, 0.02); o.end = t; }
      const buf = pluck(ac, midi), pr = Math.pow(2, (r() * 4 - 2) / 1200), src = ac.createBufferSource(), g = gain(ac, 0);
      src.buffer = buf; src.playbackRate.value = pr;
      src.connect(g); g.connect(inG);
      if (!glide && r() < 0.3) { src.playbackRate.setValueAtTime(pr * Math.pow(2, -2 / 12), t); src.playbackRate.setTargetAtTime(pr, t + 0.03, 0.022); }
      if (dur > 0.45) { const len = Math.min(2.5, dur - 0.22); src.detune.setValueCurveAtTime(vibrato(len), t + 0.22, len); }
      const A = 0.6 * Math.pow(vel, 1.2) * GTR.lead * (lead.timbre === 'bright' ? 0.85 : 1), off = t + dur, end = off + 0.09 * 7;
      g.gain.setValueAtTime(A, t);
      g.gain.setTargetAtTime(0, off, 0.09);
      src.start(t); src.stop(end);
      const v = registry.add({ src: [src], all: [src, g], start: t, end, tag, out: g });
      last = { v, src, g, A, pr, midi, now: midi, end: off + 0.09 * 4 };
      voices.push(last);
    },
    dispose(t) { bus.end = t; },
  };
}
